import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * Caps tall portal AI answers and reveals the rest via Show more / Show less.
 */
function PortalAiAnswerExpandable({ children, contentKey }) {
  const bodyRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  const measure = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    if (!expanded) {
      setOverflows(el.scrollHeight > el.clientHeight + 1);
      return;
    }
    const cap = parseFloat(getComputedStyle(el).getPropertyValue('--pai-answer-cap')) || 320;
    setOverflows(el.scrollHeight > cap + 1);
  }, [expanded]);

  useLayoutEffect(() => {
    setExpanded(false);
  }, [contentKey]);

  useLayoutEffect(() => {
    measure();
  }, [measure, children, contentKey]);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  const showToggle = overflows || expanded;

  return (
    <div className={`portal-ai-widget__a-expand${expanded ? ' is-expanded' : ''}`}>
      <div
        ref={bodyRef}
        className={`portal-ai-widget__a-body${expanded ? ' is-expanded' : ''}${
          showToggle && !expanded ? ' is-clamped' : ''
        }`}
      >
        {children}
      </div>
      {showToggle && (
        <button
          type="button"
          className="portal-ai-widget__a-more"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}

PortalAiAnswerExpandable.propTypes = {
  children: PropTypes.node.isRequired,
  contentKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

PortalAiAnswerExpandable.defaultProps = {
  contentKey: undefined,
};

export default PortalAiAnswerExpandable;
